import { ICountryAsset } from "models/Assets/ICountryAsset";
import { ICoreModels, ISeoPage } from "../types/types";
import { labelHelper } from "helpers/themeHelpers";
import { EnumScreenNames } from "../../../enums/EnumScreenNames";
import { ILiteRouterApiResponse } from "models/ILiteRouterApiResponse";
import EssentialData from "../../../private/classes/EssentialData.class";
import { getCoreModels } from "../helpers/getCoreModels";
import SeoItemModel, { getPlaceholders } from "../models/SeoItemModel";
import {IFindPageQueryString, IPostsApiResponse} from "models/post";

type TypeGenericLocalKeys =
  | "cps"
  | "countryAbb"
  | "siteName"
  | "marketName"
  | "hostNoSubDomain"
  | "hostFull"
  | "currentFullUrl"
  | "location"
  | "current"
  | "lang"
  | "lang2"
  | "countryName"
  | "year"
type TypeCustomLocalKeys =
  | "ads_number"
  | "shops_number"
  | "monthly_installment_number"
  | "premium_ad_number"
  | "__model1__"
  | "__model2__"
  | "__model3__"
  | "city"
  | "adsCount"
  | "memberName"
  | "memberId"
  | "cityName"
  | "cityUrl"
  | "page"
  | "totalPage"
  | "categoryName"
  | "categoryUrl"
  | "pageName"
  | "post_hash"
  | "categoryUrlLang2"
  | "url_name"
  | "current"
  | "country"
  | "max_price"
  | "min_price"
  | "avg_price"
  | "body_type"
  | "weight"
  | "origin"
  | "doors"
  | "engine"
  | "transmission"
  | "address"
  | "neighborhood"
  | "phone_number"
  | "hours"
  | "workingDays"
  | "services"
  | "Sector_JobSeeker"
  | "cars"
  | "fax"
  | "currency"
  | "related"
  | "off"
  | "query";


type TypeSeoFieldNames =
  | "pageMetaDescription"
  | "pageTitle"
  | "link2"
  | "shopName"
  | "pageH1"
  | "canonical";

export class Localization {
  public readonly lang2: string;
  private requestUri: string;

  constructor(
    public readonly lang: string,
    public readonly country: ICountryAsset = null,
    public readonly hostNoSubDomain: string,
    public readonly fullHostName: string
  ) {
    this.lang2 = lang === "ar" ? "en" : "ar";
  }

  public setRequestUri(requestUri: string) {
    this.requestUri = requestUri;
  }

  private static cache = {}; //{screenKey: {}}
  private static cachePerCountry = {}; //{screenKey: {}}
  private static cacheLabels = {}; //{screenKey: {}}
  public static async initOnce(all_countries: string) {
    Localization.cacheLabels["ar"] = await import(
      `../translations/labels/ar.json`
    ).then((r) => r.default);
    Localization.cacheLabels["en"] = await import(
      `../translations/labels/en.json`
    ).then((r) => r.default);

    for (let screen of Object.keys(EnumScreenNames)) {
      if (!Localization.cache[screen]) {
        Localization.cache[screen] = await import(
          `../translations/screens/${screen}/default.json`
        )
          .then((r) => r.default)
          .catch(() => null);

        for (const countryCode of all_countries.split("|")) {
          Localization.cachePerCountry[screen] =
            Localization.cachePerCountry[screen] || {};
          Localization.cachePerCountry[screen][countryCode] = await import(
            `../translations/screens/${screen}/${countryCode}.json`
          )
            .then((r) => r.default)
            .catch(() => null);
        }
      }
    }
  }

  private localizationReplace(
    type: string,
    data: ISeoPage,
    payload?: ICoreModels
  ): string {
    let fullString: string;
    if (typeof data !== "string") {
      fullString = JSON.stringify(data);
    } else {
      fullString = data;
    }
    if (process.env.NEXT_PUBLIC_CUSTOM_THEME == "shobiddak") {
      fullString = fullString.replaceAll(
        /\bps\.[^\/\s]+/g,
        "www." + this.hostNoSubDomain
      );
      fullString = fullString.replaceAll(
        "__website__",
        this.lang == "en" ? "ShoBiddak" : "شو بدك"
      );
    } else {
      fullString = fullString.replaceAll(
        /\bps\.[^\/\s]+/g,
        "ps." + this.hostNoSubDomain
      );
      fullString = fullString.replaceAll(
        "__website__",
        this.lang == "en" ? "OpenSooq" : "السوق المفتوح"
      );
    }
    //return fullString;
    switch (type) {
      case "current":
        let current =
          (payload?.cpOptions && payload?.cpOptions.length > 0
            ? payload.cpOptions[payload.cpOptions.length - 1]
            : payload.subCategory) ||
          payload.category ||
          null;
        if (current) {
          fullString = fullString.replaceAll(
            "__title__",
            labelHelper(current, this.lang)
          );
          fullString = fullString.replaceAll(
            "__current__",
            labelHelper(current, this.lang)
          );
        }
        break;
      case "location":
        fullString = fullString.replaceAll(
          "__city__",
          labelHelper(payload.city, this.lang)
        );
        fullString = fullString.replaceAll(
          "__neighborhood__",
          labelHelper(payload.neighborhood || payload.city || "", this.lang)
        );
        break;
      case "countryAbb":
        return (fullString = fullString.replaceAll(
          "__country_abb__", //__country_abb__
          this.country?.abbreviation || "www"
        ));
      case "marketName":
        return (fullString = fullString.replaceAll(
          "__market_name__",
          labelHelper(payload?.city || this.country, this.lang) //سوق الاردن المفتوح
        ));
      case "siteName":
        if (this.country) {
          const countryName = labelHelper(this.country, this.lang);
          const siteNameArTemplate = `سوق ${countryName} المفتوح`;
          const siteNameEnTemplate = `Opensooq ${countryName}`;
          return (fullString = fullString.replaceAll(
            "__site_name__",
            this.lang === "ar" ? siteNameArTemplate : siteNameEnTemplate
          ));
        } else {
          return (fullString = fullString.replaceAll(
            "__site_name__",
            this.lang === "ar" ? "السوق المفتوح" : "Opensooq"
          ));
        }
      case "hostNoSubDomain":
        return (fullString = fullString.replaceAll(
          "__host_name__", //opensooq.com
          this.hostNoSubDomain
        ));
      case "hostFull":
        return (fullString = fullString.replaceAll(
          "__host_name_full__", //jo.opensooq.com
          this.fullHostName
        ));
      case "currentFullUrl":
        return (fullString = fullString.replaceAll(
          "__url__", //jo.opensooq.com
          `https://${this.fullHostName}${this.requestUri}`
        ));
      case "countryName":
        return (fullString = fullString.replaceAll(
          "__country_name__",
          labelHelper(this.country, this.lang) //Jordan/UAE
        ));
      case "lang":
        return (fullString = fullString.replaceAll("__lang__", this.lang));
      case "lang2":
        return (fullString = fullString.replaceAll("__lang2__", this.lang2));
      case "year":
        const date = new Date();
        const year = date.getFullYear().toString();
        return (fullString =fullString.replaceAll("__year__" , year));

    }
    return fullString;
  }

  public replacePlaceHolders(seoPageData: any): ISeoPage {
    let data = seoPageData as any;
    data = this.localizationReplace("countryName", data);
    data = this.localizationReplace("countryAbb", data);
    data = this.localizationReplace("marketName", data);
    data = this.localizationReplace("siteName", data);
    data = this.localizationReplace("hostNoSubDomain", data);
    data = this.localizationReplace("hostFull", data);
    data = this.localizationReplace("currentFullUrl", data);
    data = this.localizationReplace("lang", data);
    data = this.localizationReplace("lang2", data);
    data = this.localizationReplace("year", data);

    return JSON.parse(data);
  }

  public replacePlaceWithLiteRouter(
    essentialData: EssentialData,
    seoItemModel: SeoItemModel,
    seoPageData: ISeoPage,
    liteRouterResponse: ILiteRouterApiResponse,
    queryString?: IFindPageQueryString
  ): ISeoPage {
    const coreModels = getCoreModels(liteRouterResponse, essentialData);

    let placeholders = getPlaceholders(
      seoItemModel,
      this.country,
      this,
      coreModels,
      queryString?.page
    );
    let json = JSON.stringify(seoPageData);
    Object.keys(placeholders).forEach((placeholderKey: string) => {
      const placeholderValue = placeholders[placeholderKey];
      json = json.replaceAll(placeholderKey, placeholderValue.trim());
    });
    return this.replacePlaceHolders(json);
  }

  public getJson(screenKey: EnumScreenNames) {
    return {
      ...(Localization.cache[screenKey][this.lang] || {}),
      ...(Localization.cachePerCountry[screenKey][this.lang] || {}),
    };
  }

  private replacer(
    replacements: { [k in TypeCustomLocalKeys]?: string | number } = null,
    text: string
  ): string {
    if (replacements) {
      Object.keys(replacements).forEach((replacementKey) => {
        if (!text) return;
        const replacementValue = replacements[replacementKey];
        const included = text.includes(`{${replacementKey}}`);
        if (included && replacementValue === null) {
          text = "";
        } else {
          text = text.replaceAll(`{${replacementKey}}`, replacementValue);
        }
      });
    }
    return text;
  }

  public trSeoField(
    screenKey: EnumScreenNames,
    seoFieldName: TypeSeoFieldNames,
    replacements?: { [k in TypeCustomLocalKeys]?: string | number }
  ): string {
    let text = Localization.cache[screenKey][this.lang][seoFieldName];
    return this.replacer(replacements, text);
  }

  public trPlaceholder(
    screenKey: EnumScreenNames,
    labelKey: string,
    replacements?: { [k in TypeCustomLocalKeys]?: string | number },
    lang: string = null
  ): string {
    let text = Localization.cacheLabels[lang || this.lang][screenKey][labelKey];
    return this.replacer(replacements, text);
  }

  public tr(
    text: string,
    labelKey: string,
    replacements?: { [k in TypeCustomLocalKeys]?: string | number }
  ): string {
    return this.replacer(replacements, text);
  }

  public replaceCountAndDatePlaceholders(
    pageData,
    postsApiResponse: IPostsApiResponse
  ) {
    let ret = {};
    let placeHolders = {
      __ads_number__: "",
      __new_ads_number__: "",
      __day__: "",
      __month__: "",
      __year__: "",
    };
    const date = new Date();
    const day = date.getDate().toString().padStart(2, "0");
    const month =
      this.lang == "ar"
        ? date.toLocaleString("ar", { month: "long" })
        : date.toLocaleString("en", { month: "short" }).toUpperCase();
    const year = date.getFullYear().toString();
    const { totalCount, newAdCount } = postsApiResponse?._meta;
    let json;
    Object.keys(placeHolders).forEach((key) => {
      switch (key) {
        case "__ads_number__":
          ret[key] = totalCount.toString();
          break;
        case "__new_ads_number__":
          ret[key] = newAdCount
            ? newAdCount > -1
              ? newAdCount.toString()
              : "0"
            : "0";
          break;
        case "__day__":
          ret[key] = day;
          break;
        case "__month__":
          ret[key] = month;
          break;
        case "__year__":
          ret[key] = year;
          break;
      }
    });
    placeHolders = { ...placeHolders, ...ret };
    json = JSON.stringify(pageData);
    Object.keys(placeHolders).forEach((placeholderKey: string) => {
      const placeholderValue = placeHolders[placeholderKey];
      json = json.replaceAll(placeholderKey, placeholderValue.trim());
    });
    return JSON.parse(json);
  }
}
